import { Spacing } from '@persona-id/design-tokens';
import { ComponentProps, ReactNode, useCallback, useRef, useState } from 'react';
import styled from 'styled-components';

import ClickableItem from 'dashboard/components/ClickableItem';
import Tooltip from 'dashboard/components/Tooltip';
import { Z_INDICES, ZIndex } from 'dashboard/styles/variables';
import menuItemMixins from 'design-system/mixins/menuItem';
import { BASE } from 'design-system/variables/spacing';
import { FontWeight } from 'design-system/variables/text';
import { useEscListener } from 'lib/hooks/useKeyListener';

const DropdownTooltip = styled(Tooltip)<{
  elevation?: ZIndex;
  maxHeight?: string;
}>`
  padding: ${Spacing[2]};
  z-index: ${({ elevation }) => elevation ?? Z_INDICES.dropdown};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  max-height: ${({ maxHeight }) => maxHeight};
  overflow-y: auto;
`;

const DropdownContainer = styled('div').attrs(() => ({
  role: 'dropdown',
}))`
  min-width: ${BASE * 10}px;
  display: flex;
  flex-direction: column;
`;

interface DropdownItemProps {
  danger?: boolean;
  variant?: 'basic' | 'primary' | 'danger';
}

const DropdownItem = styled(ClickableItem).attrs(() => ({
  tabIndex: 0,
}))<DropdownItemProps>`
  height: ${({ theme }) => theme.element.height.md};
  padding: 0 ${Spacing[6]} 0 ${Spacing[2]};
  min-width: 120px;
  font-weight: ${FontWeight.Medium};

  :hover,
  :focus {
    ${menuItemMixins.hover};
  }
`;

DropdownItem.defaultProps = {
  variant: 'primary',
};

type DropdownRenderProps = (props: {
  isOpen: boolean;
  openDropdown: () => void;
  closeDropdown: () => void;
}) => ReactNode;

export interface DropdownProps extends Pick<ComponentProps<typeof Tooltip>, 'placement'> {
  className?: string;
  children: DropdownRenderProps;
  items: ReactNode[];
  triggerOnHover?: boolean;
  elevation?: ZIndex;
  tooltipMaxheight?: string;
}

/**
 * A content agnostic dropdown component. This component only handles the
 * dropdown open/close logic and basic rendering -- actual item functionality
 * is up to the user.
 */
const Dropdown = ({
  className,
  children,
  items,
  placement = 'right-start',
  triggerOnHover,
  elevation = Z_INDICES.dropdown,
  tooltipMaxheight,
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const isOpenRef = useRef<number>(null);

  const openDropdown = useCallback(() => {
    if (isOpenRef.current != null) {
      clearTimeout(isOpenRef.current);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      isOpenRef.current = null;
    }
    setIsOpen(true);
  }, []);
  const closeDropdown = useCallback(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    isOpenRef.current = window.setTimeout(() => setIsOpen(false), 75);
  }, []);

  useEscListener(closeDropdown);

  return (
    <DropdownTooltip
      elevation={elevation}
      className={className}
      placement={placement}
      hideArrow={true}
      tooltipShown={isOpen}
      enablePopoverClickEvents={true}
      maxHeight={tooltipMaxheight}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [-7, BASE * -1],
          },
        },
      ]}
      tooltip={
        <DropdownContainer
          onMouseOver={triggerOnHover ? openDropdown : undefined}
          onMouseOut={triggerOnHover ? closeDropdown : undefined}
        >
          {items}
        </DropdownContainer>
      }
    >
      {children({ isOpen, openDropdown, closeDropdown })}
    </DropdownTooltip>
  );
};

export default Dropdown;
